import { CommonTooltips } from "@mcleod/common";
import { Label, Table, TableColumn, TableRow, TableRowDisplayEvent, Textbox } from "@mcleod/components";
import { CurrencySettings, HorizontalAlignment, ModelRow, StringUtil } from "@mcleod/core";
import { SecondaryFilterProps } from "./planning/FilterPanel";

export class TariffManagementUtil {

    static readonly ACTIVE = {
        id: "active",
        caption: "Active",
        filter: (row: ModelRow): boolean => {
            const expireTimestamp = row.get("expire_timestamp");
            if (!expireTimestamp) {
                return true;
            }
            const expireDate = new Date(expireTimestamp);
            const currentDate = new Date();
            return expireDate >= currentDate;
        }
    };

    static readonly EXPIRE = {
        id: "expire",
        caption: "Expired",
        filter: (row: ModelRow): boolean => {
            const expireTimestamp = row.get("expire_timestamp");
            if (!expireTimestamp) {
                return false;
            }
            const expireDate = new Date(expireTimestamp);
            const currentDate = new Date();
            return expireDate <= currentDate;
        }
    };

    public static getOrdersSecondaryFilters(): SecondaryFilterProps[] {
        return [TariffManagementUtil.ACTIVE, TariffManagementUtil.EXPIRE];
    }

    /**
     * This method generates a formatted export name for the tariff management table.
     * It uses the current date and formats it in the mm_dd_year format (e.g., Tariff_Management_02_17_2025),
     *
     * padStart(2, '0') ensures that the day and month are always two digits long.
     * For example: If the day is a single digit (e.g., 7), it will be converted to '07'.
     */
    public static dateStamp(): string {
        const date = new Date();
        const currentDate = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const dateStamp = `${month}_${currentDate}_${year}`;
        return dateStamp;
    }

    static readonly columnsToShowLeftAlign = ["Charge Type", "Miles", "Weight", "Pieces", "Spots", "Rate", "Line Item ID",
        "Average Weight Pallet", "Handling Units", "Freight Value", "Cube", "Linear", "CF1", "CF2",
        "Minimum", "Maximum", "Priority", "Effective", "Expire", "Line Item ID", "Currency"];

    static readonly columnsToShowRightAlign = ["Miles", "Weight", "Pieces", "Spots", "Rate", "Average Weight Pallet", "Handling Units",
        "Cube", "Linear", "Minimum", "Maximum", "Line Item ID", "CF2", "Priority"];

    static readonly staticColumns = [
        { columnCaption: "Tariff ID" },
        { columnCaption: "Charge Type" },
        { columnCaption: "Method" },
        { columnCaption: "Rate" },
        { columnCaption: "Always" },
        { columnCaption: "Priority" },
        { columnCaption: "Effective" },
        { columnCaption: "Expire" },
        { columnCaption: "Line Item ID" },
        { columnCaption: "Notes" }
    ];

    static readonly columnsToCheck = [
        { field: "service_type_code", columnCaption: "Order Type" },
        { field: "carrier_scac", columnCaption: "Carrier SCAC" },
        { field: "compare_option_code", columnCaption: "Option" },
        { field: "conveyance_type_code", columnCaption: "Conveyance" },
        { field: "ship_reg_name", columnCaption: "Region From" },
        { field: "cons_reg_name", columnCaption: "Region To" },
        { field: "reversible_flag", columnCaption: "Rate >=<" },
        { field: "miles", columnCaption: "Miles" },
        { field: "weight", columnCaption: "Weight" },
        { field: "pieces", columnCaption: "Pieces" },
        { field: "spots", columnCaption: "Spots" },
        { field: "average_pallet_weight", columnCaption: "Average Weight Pallet" },
        { field: "handling_units", columnCaption: "Handling Units" },
        { field: "freight_value", columnCaption: "Freight Value" },
        { field: "linear_units", columnCaption: "Linear" },
        { field: "cubic_units", columnCaption: "Cube" },
        { field: "nmfc", columnCaption: "Class" },
        { field: "product_sku", columnCaption: "Product SKU" },
        { field: "qual_factor_1", columnCaption: "QF1" },
        { field: "qual_factor_2", columnCaption: "QF2" },
        { field: "calc_factor", columnCaption: "CF1" },
        { field: "calc_factor_2", columnCaption: "CF2" },
        { field: "minimum_charge", columnCaption: "Minimum" },
        { field: "maximum_charge", columnCaption: "Maximum" },
        { field: "tli_uid", columnCaption: "Line Item ID" },
        { field: "currency_type", columnCaption: "Currency" },
    ];

    public static setTableColumnAlignmentToLeft(table: Table) {
        const leftAlignment = HorizontalAlignment.LEFT;
        const rightAlignment = HorizontalAlignment.RIGHT;
        const columnsLeft = TariffManagementUtil.columnsToShowLeftAlign;
        const columnsRight = TariffManagementUtil.columnsToShowRightAlign;
        columnsLeft.forEach((columnCaption) => {
            const column = table.getColumnByCaption(columnCaption);
            if (column) {
                column.headingCell.align = columnsRight.includes(columnCaption) ? rightAlignment : leftAlignment;
                column.cellDef.cellProps.align = columnsRight.includes(columnCaption) ? rightAlignment : leftAlignment;
            }
        });
    }

    public static showColumnsForEmptyData(table: Table, isFromAdjustRate: boolean): void {
        const columnsToShow = TariffManagementUtil.staticColumns.map(c => c.columnCaption);
        columnsToShow.forEach(columnCaption => {
            const column = table.getColumnByCaption(columnCaption);
            if (column) {
                table.setColumnVisibility(column, true);
            }
        });
        const allColumns = isFromAdjustRate ? table.columns : table.columns.slice(1);
        allColumns.forEach(column => {
            if (!columnsToShow.includes(column.headingCell.caption)) {
                table.setColumnVisibility(column, false);
            }
        });
    }

    public static dynamicallyShowColumn(table: Table, modelRows: ModelRow[]) {
        const columnsToCheck = TariffManagementUtil.columnsToCheck;
        const isMultiCurrencyEnabled = CurrencySettings.getSingleton()?.multiCurrencyEnabled();
        columnsToCheck.forEach(({ field, columnCaption }) => {
            let showColumn = false;
            for (const row of modelRows) {
                if (field === "reversible_flag") {
                    if (row.get(field) === true) {
                        showColumn = true;
                        break;
                    }
                } else if (field === "currency_type") {
                    if (isMultiCurrencyEnabled && row.get(field)) {
                        showColumn = true;
                        break;
                    }
                } else {
                    if (row.get(field)) {
                        showColumn = true;
                        break;
                    }
                }
            }
            const column: TableColumn = table.getColumnByCaption(columnCaption);
            table.setColumnVisibility(column, showColumn);
        });
    }

    public static tableRowDisplayLogic(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        const labelDeficitRating = tableRow.findComponentById("labelAlways") as Label;
        const textboxRateEitherDirection = tableRow.findComponentById("labelRateEitherDirection") as Label;
        const textboxNotes = tableRow.findComponentById("textboxNotes") as Textbox;
        const deficitRating: boolean = modelRow.get("charge_regardless_flag");
        const rateReversibleFlag: boolean = modelRow.get("reversible_flag");
        const rateNote: string = modelRow.get("rate_note");
        if (rateNote && rateNote.length > 60) {
            const displayRateNote = rateNote.substring(0, 60) + " ...";
            textboxNotes.text = `${displayRateNote} Read More`;
        }
        labelDeficitRating.text = deficitRating ? "Yes" : "No";
        textboxRateEitherDirection.text = rateReversibleFlag ? "Yes" : "No";
        this.addHoverToTariffRegionComponents(tableRow, modelRow);
        this.addScreenCodesHoverToDiscover(tableRow, modelRow);
    }

    private static addHoverToTariffRegionComponents(tableRow: TableRow, modelRow: ModelRow<any>) {
        const components = [
            { componentId: "textboxRegionFrom", fieldName: "ship_reg_name" },
            { componentId: "textboxRegionTo", fieldName: "cons_reg_name" }
        ];
        components.forEach(({ componentId, fieldName }) => {
            const regionName = modelRow.get(fieldName);
            const textbox = tableRow.findComponentById(componentId) as Textbox;
            if (regionName && !StringUtil.isEmptyString(regionName)) {
                CommonTooltips.setTooltipContentLayout(textbox, null, "lme/dispatch/TariffRegionQuickInfo", null, { region_name: regionName });
            }
        });
    }

    private static addScreenCodesHoverToDiscover(tableRow: TableRow, modelRow: ModelRow<any>) {
        const components = [
            { componentId: "textboxMethod", fieldName: "calc_uom_code" },
            { componentId: "textboxConveyance", fieldName: "conveyance_type_code" },
            { componentId: "textboxQF1", fieldName: "qual_factor_1" },
            { componentId: "textboxQF2", fieldName: "qual_factor_2" },
            { componentId: "textboxClass", fieldName: "nmfc_class_code" },
            { componentId: "textboxClass", fieldName: "nmfc" },
            { componentId: "textboxOption", fieldName: "compare_option_code" }
        ];
        components.forEach(({ componentId, fieldName }) => {
            const fieldCode = modelRow.get(fieldName);
            const fieldname = fieldName === "nmfc" ? "nmfc_class_code" : fieldName === "conveyance_type_code" ? "tli_conveyance_type_code" : fieldName;
            const textbox = tableRow.findComponentById(componentId) as Textbox;
            if (fieldCode && !StringUtil.isEmptyString(fieldCode)) {
                CommonTooltips.setTooltipContentLayout(textbox, null, "lme/dispatch/ScreenCodesQuickInfo", null, { field_code: fieldCode, field_name: fieldname });
            }
        });
    }

    public static setCurrencyTextboxState(textbox: Textbox) {
        const isMultiCurrencyEnabled = CurrencySettings.getSingleton()?.multiCurrencyEnabled();
        textbox.enabled = isMultiCurrencyEnabled;
        textbox.visible = isMultiCurrencyEnabled;
        textbox.required = isMultiCurrencyEnabled;
    }
}
